/**
 * Базовые расчёты диагностики: разбор текущего состояния бизнеса (точка А).
 *
 * Тождество, на котором держится вся модель:
 *   клиенты = обращения × конверсия
 *   продажи = клиенты × частота
 *   выручка = продажи × средний чек
 * то есть  R = L × C × P × Q
 */

import { LEVER } from './levers';
import { getNiche } from '../../../data/diagnostic/niches';
import { getDealModel } from '../../../data/diagnostic/dealModels';

export const toFiniteNumber = (value, fallback = 0) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const isPositive = (value) => Number.isFinite(value) && value > 0;

/**
 * Частота покупок за расчётный период.
 * По умолчанию берётся из модели сделки (значения там годовые) и приводится к месяцу.
 */
export const resolveFrequency = ({ nicheId, dealModelId, frequency }) => {
  const explicit = toFiniteNumber(frequency, 0);
  if (isPositive(explicit)) {
    return { value: explicit, source: 'user' };
  }

  const niche = getNiche(nicheId);
  const dealModel = getDealModel(dealModelId || (niche ? niche.defaultDealModel : null));
  const perYear = dealModel.defaultFrequency;
  const perMonth = perYear / 12;

  return {
    value: Math.max(perMonth, 0.1),
    source: 'dealModel',
    perYear,
  };
};

/**
 * Разбор текущего состояния.
 *
 * @param {object} input
 * @param {number} input.revenue  выручка за период
 * @param {number} input.sales    число продаж за период
 * @param {number} input.leads    число обращений за период (может отсутствовать)
 * @param {number} input.frequency сколько раз клиент покупает за период
 */
export const calculateCurrentState = (input) => {
  const revenue = toFiniteNumber(input.revenue, 0);
  const sales = toFiniteNumber(input.sales, 0);
  const leads = toFiniteNumber(input.leads, 0);

  const warnings = [];
  const errors = [];

  if (!isPositive(revenue)) {
    errors.push({
      code: 'REVENUE_REQUIRED',
      text: 'Укажите выручку за период — без неё расчёт невозможен.',
    });
  }

  if (!isPositive(sales)) {
    errors.push({
      code: 'SALES_REQUIRED',
      text: 'Укажите число продаж за период — из него считается средний чек.',
    });
  }

  const frequencyInfo = resolveFrequency(input);
  const frequency = Math.max(frequencyInfo.value, 0.1);

  if (errors.length) {
    return { valid: false, errors, warnings, revenue, sales, leads };
  }

  const averageCheck = revenue / sales;
  const clients = sales / frequency;

  let conversion = null;
  let hasConversion = false;

  if (isPositive(leads)) {
    conversion = clients / leads;
    hasConversion = true;

    if (conversion > 1) {
      warnings.push({
        code: 'CONVERSION_ABOVE_100',
        text:
          'Продаж получилось больше, чем обращений. Возможно, часть клиентов приходит без заявки — ' +
          'например, заходят сами. Расчёт по потоку обращений будет приблизительным.',
      });
      conversion = 1;
    }
  } else {
    warnings.push({
      code: 'NO_LEADS',
      text:
        'Вы не указали число обращений. Посчитаем без рычага «конверсия» — ' +
        'если добавите эту цифру, расчёт станет точнее.',
    });
  }

  const effectiveLeads = hasConversion ? leads : clients;
  const effectiveConversion = hasConversion ? conversion : 1;

  return {
    valid: true,
    errors,
    warnings,
    revenue,
    sales,
    leads: effectiveLeads,
    clients,
    averageCheck,
    conversion: effectiveConversion,
    conversionPercent: effectiveConversion * 100,
    frequency,
    frequencySource: frequencyInfo.source,
    hasConversion,
    /** Текущие значения рычагов — вход для сценариев. */
    levers: {
      [LEVER.LEADS]: effectiveLeads,
      [LEVER.CONVERSION]: effectiveConversion,
      [LEVER.PRICE]: averageCheck,
      [LEVER.FREQUENCY]: frequency,
    },
    /** Какие рычаги реально доступны для роста по введённым данным. */
    availableLevers: hasConversion
      ? [LEVER.PRICE, LEVER.CONVERSION, LEVER.LEADS, LEVER.FREQUENCY]
      : [LEVER.PRICE, LEVER.LEADS, LEVER.FREQUENCY],
  };
};

/**
 * Проверка тождества R = L × C × P × Q. Используется в тестах и отладке.
 */
export const verifyIdentity = (state) => {
  if (!state.valid) return { ok: false, diff: null };

  const restored =
    state.levers.l * state.levers.c * state.levers.p * state.levers.q;
  const diff = Math.abs(restored - state.revenue);

  return { ok: diff < Math.max(1, state.revenue * 1e-6), diff, restored };
};

/** Дополнительные показатели, если клиент указал рекламный бюджет. */
export const calculateAdMetrics = (state, adBudget) => {
  const budget = toFiniteNumber(adBudget, 0);

  if (!isPositive(budget) || !state.valid) {
    return null;
  }

  const costPerClient = isPositive(state.clients) ? budget / state.clients : null;
  const costPerLead = isPositive(state.leads) ? budget / state.leads : null;
  const adShare = (budget / state.revenue) * 100;

  return {
    budget,
    costPerClient,
    costPerLead,
    adShare,
    revenuePerAdTenge: budget > 0 ? state.revenue / budget : null,
  };
};
